import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { ClassNameMap } from "@material-ui/core/styles/withStyles";
import Tooltip from "@material-ui/core/Tooltip";
import CancelIcon from "@material-ui/icons/CancelRounded";
import SuccessIcon from "@material-ui/icons/CheckCircleRounded";
import ErrorIcon from "@material-ui/icons/ErrorRounded";
import clsx from "clsx";
import prettyBytes from "pretty-bytes";
import React from "react";
import { Chip } from "../../interaction";
import { Color } from "../../theme";
import { HasTestId, PlainFunctionComponent } from "../../types";
import { doNothing, empty, identity, ifDefined, neverReachedFor } from "../../util";

export type UploadStatus = { type: "progress" | "valid" } | { type: "invalid"; validationErrors: string[] };

export interface UploadFileListSubElement {
    endAdornment?: React.ReactNode;
    file: File;
    /**
     * This property is mandatory because it is used as key. The `name` of the `file` and / or its `size` are
     * not sufficient because someone could upload the same file twice.
     */
    key: string;
    onClickValidFile?: () => void;
    onRemoveFile?: () => void;
    status: UploadStatus;
}

export interface UploadFileListProps extends HasTestId {
    files: UploadFileListSubElement[];
    /**
     * Locale for pretty printing file sizes. Set it to `false` for no localization, to `true` for using the
     * system/browser locale or to a [BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag),
     * e.g. `en`, `de`, ...
     *
     * @see prettyBytes#Options#locale
     */
    locale: string | boolean;
}

export const UploadFileList: PlainFunctionComponent<UploadFileListProps> = ({ files, locale, testId }) => {
    const classes = useStyles();
    return (
        <Box className={classes.fileList} data-testid={testId}>
            {files.map((subElement: UploadFileListSubElement) => (
                <Chip
                    key={subElement.key}
                    actionIcon={<CancelIcon />}
                    className={clsx(classes.fileListChip, {
                        [classes.progress]: subElement.status.type === "progress",
                        [classes.invalid]: subElement.status.type === "invalid",
                        [classes.valid]: subElement.status.type === "valid",
                    })}
                    // do not use as `<StatusIcon ... />` because this destroys "& >" styling used for `MuiChip` in global theme, therefore also hooks cannot be used inside it
                    icon={StatusIcon({ classes, status: subElement.status })}
                    label={<FileLabel locale={locale} subElement={subElement} />}
                    onAction={ifDefined(subElement.onRemoveFile, identity)}
                />
            ))}
        </Box>
    );
};

interface StatusIconProps {
    classes: ClassNameMap<"circularProgress" | "invalid" | "valid">;
    status: UploadStatus;
}

const StatusIcon = ({ classes, status }: StatusIconProps): React.ReactElement => {
    switch (status.type) {
        case "invalid":
            return <ErrorIcon key="errorIcon" className={classes.invalid} />;
        case "progress":
            return <CircularProgress key="progressIcon" className={classes.circularProgress} size={16} />;
        case "valid":
            return <SuccessIcon key="successIcon" className={classes.valid} />;
        default:
            return neverReachedFor(status);
    }
};

interface FileLabelProps {
    subElement: UploadFileListSubElement;
    locale: string | boolean;
}

const FileLabel = ({ subElement, locale }: FileLabelProps) => {
    const isValid = subElement.status.type === "valid";
    const label = subElement.file.name + generateLabelAddition(subElement, locale);
    const onClick = isValid ? ifDefined(subElement.onClickValidFile, (onClick) => () => onClick(), doNothing) : doNothing;

    const classes = useStyles();
    return (
        <Box alignItems="center" display="flex">
            <Tooltip title={label}>
                <span className={clsx(classes.fileLabel, { [classes.fileListPointer]: isValid })} onClick={onClick}>
                    {label}
                </span>
            </Tooltip>
            <Box className={classes.fileLabelEndAdornment}>{subElement.endAdornment}</Box>
        </Box>
    );
};

const generateLabelAddition = (file: UploadFileListSubElement, locale: string | boolean): string => {
    switch (file.status.type) {
        case "progress":
            return "";
        case "invalid":
            return empty(file.status.validationErrors) ? " ()" : ` (${file.status.validationErrors.reduce((p, c) => `${p}, ${c}`)})`;
        case "valid":
            return ` (${prettyBytes(file.file.size, { locale })})`;
        default:
            return neverReachedFor(file.status);
    }
};

const useStyles = makeStyles(
    createStyles({
        fileList: {
            // use "matcher" for Material UI CSS classes as they might change if e.g. nesting themes, see https://material-ui.com/customization/components/#global-css-override
            "& > [class*='MuiChip-root']": {
                marginTop: 8,
            },
        },
        fileListChip: {
            width: "100%",
        },
        fileListPointer: {
            cursor: "pointer",
        },
        progress: {
            color: Color.grey4,
        },
        circularProgress: {
            color: Color.blue1, // override Mui-Chip-icon `color` and restore default
            marginLeft: 10, // override MuiChip-icon `marginLeft` for circular progress in order to align with other icons
        },
        valid: {
            color: Color.blue1,
        },
        invalid: {
            color: Color.red1,
        },
        fileLabel: {
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
        fileLabelEndAdornment: {
            paddingLeft: 16,
            paddingRight: 8, // plus 8px from Chip's action
        },
    }),
);
